import { Dialog, Divider, IconButton, Paper, Stack, Typography } from "@mui/material";
import { closeBranchDrawer, setActiveBranch, selectedBranch } from "@/store/uiSlice";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { RootState } from "@/store/store";
import { useMemo } from "react";
import { isAvailableToday, checkServiceAvailability } from "@/utils/weekDays";
import { Branch, Schedule } from "@/types/branch";
import { useFetchBranchQuery } from "@/features/organizations/api/branchApi";
import BranchCard from "../../BranchCard";
import { setBranch } from "@/features/order/slice";
import { MENU_FLOWS, MENU_FLOW_LABELS } from "@/constants/global";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";

interface Props {
    requestedService: MENU_FLOWS | null;
    onClose: () => void;
}

const NotAvailableBranchDialog = ({ requestedService, onClose }: Props) => {
    const dispatch = useAppDispatch();
    const activeBranch = useAppSelector((state: RootState) => selectedBranch(state));
    const organizationId = useAppSelector((state) => state.ui.activeOrganization);

    const { data: branches = [] } = useFetchBranchQuery(organizationId!);

    const activeBranches = useMemo(() => {
        const availableBranches = branches.filter((branch) => {
            const isBranchOpen = checkServiceAvailability({
                isEnabled: true,
                schedule: branch?.schedule,
            });
            const hasService = branch.services?.includes(requestedService as string);
            const isNotActive = branch.id !== activeBranch?.id;
            return isBranchOpen && hasService && isNotActive;
        });
        return availableBranches;
    }, [branches, activeBranch, requestedService]);

    const handleSelect = (branch: Branch) => {
        dispatch(setActiveBranch(branch.id));
        dispatch(setBranch({
            id: branch.id,
            title: branch.title,
            location: branch.location,
            whatsapp: branch.whatsapp,
        }));
        localStorage.setItem("branchId", branch.id);
        dispatch(closeBranchDrawer());
        onClose();
    };

    return (
        <Dialog open={!!requestedService} onClose={onClose} fullWidth maxWidth="xs">
            <Stack direction="row" justifyContent="space-between" alignItems="center" px={3} py={2}>
                <Typography variant="h3">{activeBranch?.title}</Typography>
                <IconButton onClick={onClose} color="primary" size="medium" component={Paper} elevation={2} sx={{ backgroundColor: "#ffffff" }}>
                    <HugeiconsIcon icon={Cancel01Icon} />
                </IconButton>
            </Stack>
            <Divider />
            {activeBranches.length > 0 ? (
                <Stack spacing={2} padding={3} pb={4}>
                    <Typography variant="body1" textAlign="center">
                        La sucursal seleccionada se encuentra cerrada en este momento. Para continuar elige otra sucursal que esté abierta:
                    </Typography>
                    {activeBranches.map((b) => (
                        <BranchCard
                            key={b.id}
                            branch={b}
                            onClick={handleSelect}
                            isActive={activeBranch?.id === b.id}
                            highlightService={requestedService as MENU_FLOWS}
                        />
                    ))}
                </Stack>
            ) : (
                <Stack spacing={2} padding={3} pb={4}>
                    <Typography variant="body1" textAlign="center">
                        Lo sentimos, por el momento no hay sucursales disponibles para el servicio <strong>{requestedService ? MENU_FLOW_LABELS[requestedService as MENU_FLOWS] : "..."}</strong>.
                    </Typography>
                </Stack>
            )}
        </Dialog>
    );
};

export default NotAvailableBranchDialog;
